import React, { useState, useEffect } from 'react'
import cn from 'classnames'
import { values, isNumber, isEmpty } from 'lodash'

import OrderInformation from './OrderInformation'
import TabOrderTotals from '../TabOrderTotals'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import TipEntryModal from '../../TipEntryModal'

import { centsToDollars } from '../../../utils/formatters'

import styles from './Details.module.scss'

const makeTipEntryModalTitle = (maxGratuity) => {
  if (isNumber(maxGratuity) && maxGratuity >= 0) {
    return `${centsToDollars(maxGratuity)} maximum amount`
  } else {
    return ''
  }
}

const isUsableNumber = (value) => isNumber(value) && value > 0

const Finalize = ({
  orderNumber,
  paymentPreference,
  onMount,
  modifiedAt,
  itemCount,
  suiteInformation,
  paymentDisplay,
  pendingMutation,
  orderTotal,
  finalizeOrder,
  exemptionOverrides,
  maxGratuity,
  userNotes,
  onClose,
  className,
}) => {
  const classNames = cn(styles.details, className);
  const [showGratuityEntry, setShowGratuityEntry] = useState(false);
  const [selectedTipAmount, setSelectedTipAmount] = useState(0)
  const grandTotal = centsToDollars(orderTotal?.totalAmountInCents + selectedTipAmount)
  const orderLoading = !(pendingMutation === undefined)

  useEffect(() => {
    onMount()
  }, [])

  const handleSetNewTip = (tipInCents) => {
    const newTipAmount = isUsableNumber(maxGratuity) ? Math.min(maxGratuity, tipInCents) : tipInCents

    setSelectedTipAmount(newTipAmount)
  }

  const confirmGratuity = (
    <Button
      key="enter gratuity button"
      onClick={() => setShowGratuityEntry(true)}
      className={styles.actionButton}
      disabled={orderLoading}
    >
      Add Gratuity
    </Button>
  )

  const finalizeAmount = (
    <Button
      key="finalize amount button"
      onClick={() => finalizeOrder(selectedTipAmount, paymentPreference)}
      className={[styles.actionButton, styles.mintGreenHighlight]}
      disabled={orderLoading}
    >
      Finalize Amount
    </Button>
  )

  const tipEntryModalTitle = makeTipEntryModalTitle(maxGratuity)
  let customGratuityEntry
  if (showGratuityEntry) {
    customGratuityEntry = (
    <TipEntryModal
      key="custom gratuity"
      title={tipEntryModalTitle}
      errorMessage={tipEntryModalTitle}
      initialValue={selectedTipAmount}
      maxTipInCents={maxGratuity}
      saveButtonText="Set Gratuity"
      onClose={() => setShowGratuityEntry(false)}
      onSubmit={handleSetNewTip}
    />
    )
  }

  return (
    <SidebarModal
      show
      title="Finalize Order"
      onClose={onClose}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <OrderInformation
          account={suiteInformation?.displayName}
          payment={paymentDisplay}
          amount={orderTotal?.subtotalDisplay}
          modifiedAt={isEmpty(modifiedAt) ? '' : modifiedAt}
          orderNumber={orderNumber}
          note={userNotes}
        />
        <TabOrderTotals
          className={styles.tabOrderTotals}
          exemptions={exemptionOverrides}
          discount={orderTotal?.discountDisplay}
          itemCount={itemCount}
          nonExemptableTaxRules={values(
            orderTotal?.nonExemptableTaxRules ?? []
          )}
          serviceCharge={orderTotal?.serviceChargeDisplay}
          serviceChargeName={orderTotal?.serviceChargeName ?? 'Service Charge'}
          subtotal={orderTotal?.subtotalDisplay}
          tax={orderTotal?.taxDisplay}
          tip={centsToDollars(selectedTipAmount)}
          total={grandTotal}
        />
        <div className={styles.buttonContainer}>
          {confirmGratuity}
          {finalizeAmount}
        </div>
      </div>
      {customGratuityEntry}
    </SidebarModal>
  )
}

export default Finalize
